
import { useState, useMemo } from "react";
import { Conversation } from "../types";

interface FilterState {
  searchQuery: string;
  statusFilter: 'all' | 'open' | 'closed' | 'archived';
  priorityFilter: 'all' | 'high' | 'medium' | 'low';
  participantFilter: string;
  dateRange: { from?: Date; to?: Date };
}

export const useConversationFilters = (conversations: Conversation[]) => {
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    statusFilter: 'all',
    priorityFilter: 'all',
    participantFilter: '',
    dateRange: {}
  });

  const filteredConversations = useMemo(() => {
    if (!conversations) return [];

    return conversations.filter(conversation => {
      // Search query filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesName = conversation.name.toLowerCase().includes(query);
        const matchesParticipants = conversation.participantEmails?.some(email => 
          email.toLowerCase().includes(query)
        );
        const matchesContent = conversation.lastMessage?.toLowerCase().includes(query);
        
        if (!matchesName && !matchesParticipants && !matchesContent) {
          return false;
        }
      }

      // Status filter
      if (filters.statusFilter !== 'all' && conversation.status !== filters.statusFilter) {
        return false;
      }

      // Priority filter
      if (filters.priorityFilter !== 'all') {
        const priority = getPriorityCategory(conversation.overallPriorityScore);
        if (priority !== filters.priorityFilter) {
          return false;
        }
      }

      // Participant filter
      if (filters.participantFilter) {
        const participantQuery = filters.participantFilter.toLowerCase();
        const matchesParticipant = conversation.participantEmails?.some(email =>
          email.toLowerCase().includes(participantQuery)
        );
        if (!matchesParticipant) {
          return false;
        }
      }

      // Date range filter
      if (filters.dateRange.from || filters.dateRange.to) {
        const conversationDate = new Date(conversation.timestamp);
        
        if (filters.dateRange.from && conversationDate < filters.dateRange.from) {
          return false;
        }
        
        if (filters.dateRange.to && conversationDate > filters.dateRange.to) {
          return false;
        }
      }

      return true;
    });
  }, [conversations, filters]);

  const getPriorityCategory = (score?: number): 'high' | 'medium' | 'low' => {
    if (!score) return 'low';
    if (score >= 7) return 'high';
    if (score >= 4) return 'medium';
    return 'low';
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.statusFilter !== 'all') count++;
    if (filters.priorityFilter !== 'all') count++;
    if (filters.participantFilter) count++;
    if (filters.dateRange.from || filters.dateRange.to) count++;
    return count;
  }, [filters]);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      searchQuery: '',
      statusFilter: 'all',
      priorityFilter: 'all',
      participantFilter: '',
      dateRange: {}
    });
  };

  return {
    filters,
    filteredConversations,
    activeFiltersCount,
    updateFilter,
    clearAllFilters,
    setSearchQuery: (query: string) => updateFilter('searchQuery', query),
    setStatusFilter: (status: 'all' | 'open' | 'closed' | 'archived') => updateFilter('statusFilter', status),
    setPriorityFilter: (priority: 'all' | 'high' | 'medium' | 'low') => updateFilter('priorityFilter', priority),
    setParticipantFilter: (participant: string) => updateFilter('participantFilter', participant),
    setDateRange: (range: { from?: Date; to?: Date }) => updateFilter('dateRange', range),
  };
};
